<template>
	<div class="cog-address">
		<!-- whitespace on the following line is significant -->
		<div v-if="readonly" class="cog-input is-read-only">{{ addressType === 'USAddress' ? address.FullAddress : address.FullInternationalAddress }}</div>
		<c-row v-else>
			<!-- Check autocomplete instead of useAutocomplete; switching input will defocus the input mid-entry -->
			<autocomplete
				v-if="includeProperty('Line1') && autocomplete"
				:id="id + '-line1'"
				v-model="address.Line1"
				:placeholder="$resource('address-line1')"
				:title="$resource('address-line1')"
				class="cog-input cog-address__street cog-input--full"
				:trigger-on-focus="false"
				:chameleon="chameleon"
				:popper-class="`cog-cognito cog-${scope} cog-cognito--${chameleon ? 'chameleon' : 'styled'} cog-select-popper`"
				:hide-loading="true"
				:fill-on-select="false"
				:blur-on-select="false"
				:tab-selects-suggestion="true"
				:autocomplete="browserAutocompleteState.Line1"
				:fetch-suggestions="fetchAutocompletePredictions"
				@select="handleAutocompleteSelect"
			/>
			<c-input
				v-else-if="includeProperty('Line1')"
				:id="id + '-line1'"
				v-model="address.Line1"
				:placeholder="$resource('address-line1')"
				:title="$resource('address-line1')"
				:label="$resource('address-line1')"
				:readonly="readonly"
				class="cog-address__street cog-input--full"
				:autocomplete="browserAutocompleteState.Line1"
			/>
			<c-input
				v-if="includeProperty('Line2')"
				:id="id + '-line2'"
				v-model="address.Line2"
				:placeholder="$resource('address-line2')"
				:title="$resource('address-line2')"
				:label="$resource('address-line2')"
				:readonly="readonly"
				class="cog-address__line-2 cog-input--full"
				:autocomplete="browserAutocompleteState.Line2"
			/>
			<c-input
				v-if="includeProperty('Line3')"
				:id="id + '-line3'"
				v-model="address.Line3"
				:placeholder="$resource('address-line3')"
				:title="$resource('address-line3')"
				:label="$resource('address-line3')"
				:readonly="readonly"
				class="cog-address__line-3 cog-input--full"
				:autocomplete="browserAutocompleteState.Line3"
			/>
			<c-input
				v-if="includeProperty('City')"
				:id="id + '-city'"
				v-model="address.City"
				:placeholder="$resource('address-city')"
				:title="$resource('address-city')"
				:label="$resource('address-city')"
				:readonly="readonly"
				class="cog-address__city cog-input--long"
				:autocomplete="browserAutocompleteState.City"
			/>
			<c-select
				v-if="includeProperty('State') && addressType === 'USAddress'"
				:id="id + '-state'"
				ref="State"
				v-model="address.State"
				:alternative-filter="customFilter"
				:scope="scope"
				:placeholder="$resource('address-state')"
				:title="$resource('address-state')"
				:label="$resource('address-state')"
				:options="stateOptions"
				filter-match="beginning"
				:readonly="readonly"
				:chameleon="chameleon"
				:mobile="mobile"
				class="cog-address__state cog-input--long"
				:autocomplete="browserAutocompleteState.State"
				@focus="toggleSelectBrowserAutocomplete(true)"
				@blur="toggleSelectBrowserAutocomplete(false)"
				@change.native="selectChanged"
			/>
			<c-input
				v-if="includeProperty('State') && addressType !== 'USAddress'"
				:id="id + '-international-state'"
				v-model="address.State"
				:placeholder="$resource('address-state-province-region')"
				:title="$resource('address-state-province-region')"
				:label="$resource('address-state-province-region')"
				:readonly="readonly"
				class="cog-address__state cog-input--long"
				:autocomplete="browserAutocompleteState.State"
			/>
			<c-input
				v-if="includeProperty('PostalCode') && addressType === 'USAddress'"
				:id="id + '-zip-code'"
				v-model="address.PostalCode"
				:placeholder="$resource('address-zip-code')"
				:title="$resource('address-zip-code')"
				:label="$resource('address-zip-code')"
				mask="#####-####"
				:type="mobile ? 'tel' : 'text'"
				:readonly="readonly"
				class="cog-address__postal-code cog-input--medium"
				:autocomplete="browserAutocompleteState.PostalCode"
			/>
			<c-input
				v-if="includeProperty('PostalCode') && addressType !== 'USAddress'"
				:id="id + '-postal-code'"
				v-model="address.PostalCode"
				:placeholder="$resource('address-postalcode')"
				:title="$resource('address-postalcode')"
				:label="$resource('address-postalcode')"
				:readonly="readonly"
				class="cog-address__postal-code cog-input--long"
				:autocomplete="browserAutocompleteState.PostalCode"
			/>
			<c-select
				v-if="includeProperty('Country') && addressType !== 'USAddress'"
				:id="id + '-country'"
				ref="Country"
				v-model="address.Country"
				:scope="scope"
				:placeholder="$resource('address-country')"
				:title="$resource('address-country')"
				:label="$resource('address-country')"
				:options="countryOptions"
				filter-match="beginning"
				:readonly="readonly"
				:chameleon="chameleon"
				:mobile="mobile"
				class="cog-address__country cog-input--long"
				:autocomplete="browserAutocompleteState.Country"
				@focus="toggleSelectBrowserAutocomplete(true)"
				@blur="toggleSelectBrowserAutocomplete(false)"
				@change.native="selectChanged"
			/>
		</c-row>
	</div>
</template>

<script lang="ts">
	import type { PropType } from 'vue';
	import { defineComponent } from 'vue';
	import CRow from '../Row.vue';
	import CInput from '../Input.vue';
	import CSelect from '../Select.vue';
	import { Autocomplete } from '@cognitoforms/element-ui';
	import type { IGoogleMapsLoader } from '@cognitoforms/shared-components/src/components/address/google-maps-loader';
	import { getAddressFromComponents } from '@cognitoforms/shared-components/src/components/address/autocomplete-address';
	import type { IAddress } from '@cognitoforms/shared-components/src/components/address/autocomplete-address';
	import { US_STATE_ABREVIATION_NAMES } from '@cognitoforms/shared-components/src/components/address/us-states-abbreviations';
	import type { FetchSuggestionsCallback } from '@cognitoforms/element-ui/types/autocomplete';
	import { FieldSubType } from '@cognitoforms/types/server-types/model/field-sub-type';

	import 'src/style/_dropdown.scss';

	export type AddressType = 'us' | 'international';

	const BROWSER_AUTOCOMPLETE_VALUES: Record<keyof IAddress, string> = {
		Line1: 'address-line1',
		Line2: 'address-line2',
		Line3: 'address-line3',
		City: 'address-level2',
		State: 'address-level1',
		PostalCode: 'postal-code',
		Country: 'country-name'
	};

	export default defineComponent({
		name: 'CAddress',
		components: {
			CInput,
			CSelect,
			CRow,
			Autocomplete
		},
		inject: ['$resource', 'log'],
		props: {
			readonly: {
				type: Boolean,
				required: true
			},
			address: {
				type: Object as PropType<IAddress>,
				default: () => ({
					Line1: '',
					Line2: '',
					Line3: '',
					City: '',
					State: '',
					PostalCode: '',
					Country: ''
				})
			},
			type: {
				type: String as PropType<AddressType>,
				default: 'international',
				validator: function (value) {
					return !value || (typeof value === 'string' && ['us', 'international'].indexOf(value.toLowerCase()) >= 0);
				}
			},
			properties: {
				type: String,
				default: 'Line1, Line2, City, State, PostalCode, Country'
			},
			autocomplete: {
				type: Boolean,
				required: true
			},
			googleMapsLoader: {
				type: Object as PropType<IGoogleMapsLoader>,
				default: null
			},
			chameleon: {
				type: Boolean,
				required: true
			},
			mobile: {
				type: Boolean,
				required: true
			},
			scope: {
				type: String,
				default: '0'
			},
			id: {
				type: String,
				required: true
			}
		},
		data() {
			return {
				countryOptions: this.$resource('countries').split('|').map(function (s) { return { value: s, displayValue: s }; }),
				browserAutocompleteState: {
					Line1: BROWSER_AUTOCOMPLETE_VALUES.Line1,
					Line2: BROWSER_AUTOCOMPLETE_VALUES.Line2,
					Line3: BROWSER_AUTOCOMPLETE_VALUES.Line3,
					City: BROWSER_AUTOCOMPLETE_VALUES.City,
					State: BROWSER_AUTOCOMPLETE_VALUES.State,
					PostalCode: BROWSER_AUTOCOMPLETE_VALUES.PostalCode,
					Country: BROWSER_AUTOCOMPLETE_VALUES.Country
				} as Record<keyof IAddress, string>,
				hasAutocompleteRequirements: false as boolean,
				previousAutocompleteSuggestions: [] as google.maps.places.AutocompletePrediction[]
			};
		},
		computed: {
			addressType(): FieldSubType {
				const isUSAddress = this.type && typeof this.type === 'string' && this.type.toLowerCase() === 'us';
				return isUSAddress ? FieldSubType.USAddress : FieldSubType.InternationalAddress;
			},
			addressProperties() {
				return this.properties.split(',').map(p => p.trim());
			},
			stateOptions() {
				return Object.values(US_STATE_ABREVIATION_NAMES).map(s => ({ value: s, displayValue: s }));
			},
			useAutocomplete(): boolean {
				if (this.autocomplete && this.hasAutocompleteRequirements && this.googleMapsLoader)
					return this.googleMapsLoader?.initialized() ?? false;

				return false;
			}
		},
		watch: {
			readonly: {
				immediate: true,
				handler: function(newValue) {
					if (!newValue)
						this.$nextTick(() => {
							this.toggleSelectBrowserAutocomplete(false);
						});
				}
			},
			useAutocomplete: {
				immediate: true,
				handler: function(state: boolean) {
					this.browserAutocomplete(!state);
				}
			}
		},
		beforeMount() {
			// Checks that line1 exists and city, state, and postal code are not defaulted.
			this.hasAutocompleteRequirements =
				this.includeProperty('Line1') &&
				(this.includeProperty('City') || !this.address.City) &&
				(this.includeProperty('State') || !this.address.State) &&
				(this.includeProperty('PostalCode') || !this.address.PostalCode);

			// Google Maps API error handling
			window.gm_authFailure = () => this.handleAutocompleteError(null);
		},
		methods: {
			includeProperty(propName: string): boolean {
				return this.addressProperties.indexOf(propName) >= 0;
			},
			browserAutocomplete(enabled: boolean): void {
				Object.entries(this.browserAutocompleteState).forEach(([key]) => {
					this.browserAutocompleteState[key] = enabled ? BROWSER_AUTOCOMPLETE_VALUES[key] : 'prevent';
				});
			},
			toggleSelectBrowserAutocomplete(isFocused: boolean): void {
				const targetSelect = this.addressType === FieldSubType.USAddress ? 'State' : 'Country';
				if ((!this.mobile && !this.chameleon) && this.includeProperty(targetSelect)) {
					if (isFocused) {
						this.browserAutocompleteState[targetSelect] = 'prevent';
					}
					else {
						this.browserAutocompleteState[targetSelect] = BROWSER_AUTOCOMPLETE_VALUES[targetSelect];
						// Remove element's readonly attribute so autocomplete in Chrome works
						this.$refs[targetSelect].$el.querySelector('input').removeAttribute('readonly');
					}
				}
			},
			selectChanged(e): void {
				if (this.addressType === FieldSubType.USAddress) {
					const val = e.target.value.toUpperCase();
					if (US_STATE_ABREVIATION_NAMES[val])
						this.address.State = US_STATE_ABREVIATION_NAMES[val];
					// check if value is either a valid state abbreviation or empty string, set address's state to it if so
					else if (Object.values(US_STATE_ABREVIATION_NAMES).includes(e.target.value) || e.target.value === '')
						this.address.State = e.target.value;
					else
						e.target.value = '';
				}
				// check if value is either a valid country or empty string, set address's country to it if so
				else if (this.$resource('countries').split('|').includes(e.target.value) || e.target.value === '')
					this.address.Country = e.target.value;
				else
					e.target.value = '';
			},
			// Alternative filter that includes abbreviations
			customFilter(input, label) {
				if (!input) // True when nothing is inputted - used to show everything + null case
					return true;
				else if (!label) // False when there is no label - used to show nothing aka "no matching option"
					return false;
				else { // Otherwise, show labels that match abbreviations and names
					const upperInput = input.toUpperCase();
					// Matches the abbreviation or beginning of state string
					return US_STATE_ABREVIATION_NAMES[upperInput] === label || label.toUpperCase().startsWith(upperInput);
				}
			},
			fetchAutocompletePredictions(input: string, cb: FetchSuggestionsCallback): void {
				if (!input || !input.length || !this.useAutocomplete)
					return cb([]);

				// Prevents suggestions list from "flashing" or temporarily disappearing when typing quickly
				cb(this.previousAutocompleteSuggestions);

				const hiddenCountry = !this.includeProperty('Country') ? this.address.CountryCode : null;
				let includedRegionCodes = null;
				if (this.addressType === FieldSubType.USAddress)
					includedRegionCodes = ['US'];
				else if (hiddenCountry)
					includedRegionCodes = [hiddenCountry];

				this.googleMapsLoader.fetchAutocompleteSuggestions({
					input,
					includedRegionCodes
				})
					.then(({ suggestions }) => {
						const result = suggestions?.map(place => ({ placePrediction: place.placePrediction, value: place.placePrediction.text })) ?? [];

						this.previousAutocompleteSuggestions = result;
						cb(result);
					})
					.catch(this.handleAutocompleteError);
			},
			handleAutocompleteSelect(suggestion: google.maps.places.AutocompleteSuggestion) {
				if (!this.useAutocomplete)
					return;

				this.googleMapsLoader.fetchFields({
					placeId: suggestion.placePrediction.placeId,
					fields: ['addressComponents']
				})
					.then(({ place }) => {
						getAddressFromComponents(this.address, place?.addressComponents ?? [], this.addressProperties);
					})
					.catch(this.handleAutocompleteError);
			},
			handleAutocompleteError(error: Error) {
				this.googleMapsLoader?.disableLoader();
				this.browserAutocomplete(true);
				this.log.error(error);
			}
		}
	});
</script>